import dayjs from "dayjs";
import numeral from "numeral";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef } from "react";
import { useAuth, useMessage } from "@/contexts";
import { CoinHelper, DecimalHelper, ErrorHelper, StringHelper } from "@/helpers";
import { useAppDispatch, useAppSelector } from "@/stores";
import { UserPermission } from "@/domains/user";
import { WalletCategory } from "@/domains/wallet";
import {
  TransferRead,
  TransferHelper,
  TransferService,
  TransferClassHelper,
  TransferDepositStatusI18nKeys,
} from "@/domains/transfer";
import {
  operatorMemberDepositSelects,
  operatorMemberDepositThunks,
} from "./store";
// components
import { ColumnsType, TableProps } from "antd/es/table";
import { Badge, Button, Modal, Popconfirm, Table, Tag } from "antd";
import {
  CrownOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
  EyeOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  SwapRightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { CopyIconButton } from "@/components";
import { TableTopFilters } from "./TableTopFilters";
import { CorrectModal, CorrectModalRef } from "./CorrectModal";
import { CheckModal, CheckModalRef } from "./CheckModal";

export const OperatorMemberDepositListPage = () => {
  const checkModalRef = useRef<CheckModalRef>(null);

  const correctModalRef = useRef<CorrectModalRef>(null);

  const { t } = useTranslation();

  const auth = useAuth();

  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorMemberDepositSelects.loading);

  const queryParams = useAppSelector(operatorMemberDepositSelects.queryParams);

  const transfers = useAppSelector(operatorMemberDepositSelects.transfers);

  const summary = useMemo(() => {
    return {
      count: transfers.length,
      amount: transfers.reduce(
        (sum, t) =>
          numeral(sum)
            .add(Number(t.amount) || 0)
            .value() ?? 0,
        0
      ),
      result: transfers.reduce(
        (sum, t) =>
          numeral(sum)
            .add(Number(t.result) || 0)
            .value() ?? 0,
        0
      ),
      charge: transfers.reduce(
        (sum, t) =>
          numeral(sum)
            .add(numeral(t.charge).value() ?? 0)
            .value() || 0,
        0
      ),
    };
  }, [transfers]);

  const { messageApi } = useMessage();

  useEffect(() => {
    void dispatch(operatorMemberDepositThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const getRowClassName = (record: TransferRead) => {
    const isInvalid = TransferHelper.isInvalid(record.status);
    return isInvalid ? "fg:disabled" : "";
  };

  const handleCorrectModalCheck = () => {
    void dispatch(operatorMemberDepositThunks.list(queryParams));
  };

  const handleRecover = (transferId: string) => {
    Modal.confirm({
      title: t("systems.Modal.sureToRecoverTransfer"),
      icon: <ExclamationCircleFilled />,
      async onOk() {
        try {
          await TransferService.recoverDeposit(transferId).run();
          void dispatch(operatorMemberDepositThunks.list(queryParams));
          void messageApi.success(t("systems.Modal.transferRecovered"));
        } catch (e) {
          if (ErrorHelper.isAxiosError(e)) {
            void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
          } else {
            console.log(e);
          }
          return Promise.reject();
        }
      },
    });
  };

  const handleCallback = (transferId: string) => {
    Modal.confirm({
      title: t("systems.Modal.sureToResendCallback"),
      icon: <ExclamationCircleFilled />,
      async onOk() {
        await TransferService.callback(transferId).run();
        void messageApi.success(t("systems.Modal.callbackResent"));
      },
    });
  };

  const handleShowCallbackMessage = (message: undefined | string) => {
    if (message === undefined) return;

    const payload = JSON.parse(message);
    Modal.info({
      title: t("systems.Modal.callbackRecord"),
      content: (
        <div className="font:monospace">
          <div>{payload.time}</div>
          <Tag color="success" className="mt:8 font:bold font:monospace">
            {t("systems.Modal.request")}
          </Tag>
          <div className="p:4 mt:4 f:12">
            <div className="p:2">
              <span className="mr:8 text:underline">{payload.req.url}</span>
              <CopyIconButton text={payload.req.url} />
            </div>
            <div className="p:2">
              <span className="mr:8">{payload.req.body}</span>
              <CopyIconButton text={payload.req.body} />
            </div>
          </div>
          <Tag color="processing" className="mt:8 font:bold font:monospace">
            {t("systems.Modal.response")}
          </Tag>
          <div className="p:4 mt:4 f:12">
            <div className="p:2">{payload.rsp.status}</div>
            <div className="p:2">
              <span className="mr:8">{payload.rsp.body ?? "(NO DATA)"}</span>
              <CopyIconButton text={payload.rsp.body} />
            </div>
          </div>
          <Tag color="error" className="mt:8 font:bold  font:monospace ">
            {t("systems.Modal.error")}
          </Tag>
          <div className="p:4 mt:4 f:12">
            <div className="p:2">{payload.exp}</div>
          </div>
        </div>
      ),
      okText: t("systems.Action.close"),
      width: 1000,
    });
  };

  const handleCheckModalCheck = () => {
    void dispatch(operatorMemberDepositThunks.list(queryParams));
  };

  const handleDestroy = (transferId: string) => {
    Modal.confirm({
      title: t("systems.Modal.sureToDestroyTransfer"),
      icon: <ExclamationCircleFilled />,
      okType: "danger",
      async onOk() {
        try {
          await TransferService.destroy(transferId).run();
          void dispatch(operatorMemberDepositThunks.list(queryParams));
          void messageApi.success(t("systems.Modal.transferDestroyed"));
        } catch (e) {
          if (ErrorHelper.isAxiosError(e)) {
            void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
          } else {
            console.log(e);
          }
          return Promise.reject();
        }
      },
    });
  };

  const handleMute = (transferId: string) => {
    Modal.confirm({
      title: t("systems.Modal.sureToMuteTransfer"),
      icon: <ExclamationCircleFilled />,
      okType: "danger",
      async onOk() {
        try {
          await TransferService.mute(transferId).run();
          void dispatch(operatorMemberDepositThunks.list(queryParams));
          void messageApi.success(t("systems.Modal.transferMuted"));
        } catch (e) {
          if (ErrorHelper.isAxiosError(e)) {
            void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
          } else {
            console.log(e);
          }
          return Promise.reject();
        }
      },
    });
  };

  const columns: ColumnsType<TransferRead> = [
    {
      width: 70,
      className: "v:top",
      title: t("domains.Transfer.id"),
      dataIndex: "id",
      render(_, { id }) {
        return (
          <div className="word-break:break-all">
            <span className="mr:4">{id}</span>
            <CopyIconButton text={id} />
          </div>
        );
      },
    },
    {
      width: 260,
      className: "v:top",
      title: `${t("domains.Transfer.belongName")} | ${t(
        "domains.Transfer.orderId"
      )} | ${t("domains.Transfer.memberInfo")}`,
      key: "order",
      render(_, { belong, order, member }) {
        return (
          <>
            <div className="word-break:break-all">
              <CrownOutlined className="mr:8" />
              {belong?.name && StringHelper.getLength(belong?.name) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisEnd(belong?.name, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.belongName")}
                    description={belong?.name}
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <CopyIconButton text={belong?.name} />
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {belong?.name ?? "?"}
                  </span>
                  <CopyIconButton text={belong?.name} />
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <ShoppingOutlined className="mr:8" />
              {order?.id && StringHelper.getLength(order?.id) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisMiddle(order?.id, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.orderId")}
                    description={
                      <>
                        <span className="mr:8 mb:8">{order?.id}</span>
                        <CopyIconButton text={order?.id} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined className="abs right:3 mt:4" />
                  </Popconfirm>
                  <CopyIconButton text={order?.id} />
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {order?.id ?? "?"}
                  </span>
                  <CopyIconButton text={order?.id} />
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <IdcardOutlined className="mr:8" />
              {member?.name && StringHelper.getLength(member?.name) > 28 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.ellipsisEnd(member?.name, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.memberName")}
                    description={
                      <>
                        <span className="mr:8 mb:8">{member?.name}</span>
                        <CopyIconButton text={member?.name} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.name ?? "?"}`}
                  </span>
                  <CopyIconButton text={member?.name} />
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <UserOutlined className="mr:8" />
              {member?.username &&
              StringHelper.getLength(member?.username) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisEnd(member?.username, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.memberUsername")}
                    description={
                      <>
                        <span className="mr:8 mb:8">{member?.username}</span>
                        <CopyIconButton text={member?.username} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.username ?? "?"}`}
                  </span>
                  <CopyIconButton text={member?.username} />
                </>
              )}
            </div>
          </>
        );
      },
    },
    {
      width: 340,
      className: "v:top",
      key: "address",
      title() {
        return (
          <div className="flex gap:8">
            {t("domains.Transfer.currency")} |{" "}
            {t("domains.Transfer.exchangeRate")} |{" "}
            {t("domains.Transfer.sourceAddress")} <SwapRightOutlined />{" "}
            {t("domains.Transfer.targetAddress")}
          </div>
        );
      },
      render(_, record) {
        const renderAddressTag = (category: number | undefined) => {
          if (category == undefined || category == null) {
            return <Tag>{t("domains.Wallet.categories.Unmarked")}</Tag>;
          }
          switch (category) {
            case WalletCategory.OperatorCollect:
              return (
                <Tag color="volcano">
                  {t("domains.Wallet.categories.OperatorCollect")}
                </Tag>
              );
            case WalletCategory.OperatorReceive:
              return (
                <Tag color="volcano">
                  {t("domains.Wallet.categories.OperatorReceive")}
                </Tag>
              );
            case WalletCategory.External:
              return (
                <Tag color="purple">
                  {t("domains.Wallet.categories.External")}
                </Tag>
              );
            default:
              return <></>;
          }
        };

        return (
          <div>
            {record.currency}
            {parseFloat(record.rate).toFixed(0) != "1" && (
              <Badge
                className="ml:8"
                count={record.rate}
                overflowCount={999999}
                color="#53a168"
              />
            )}
            <div className="flex">
              {record.source?.address ? (
                <>
                  {record.source?.address != null &&
                  (record.source?.address.length != 34 ||
                    record.source?.address[0] != "T") ? (
                    <span className="mr:4 fg:gray-60 text:line-through">
                      {record.source?.address}
                    </span>
                  ) : (
                    <>
                      <a
                        target="_blank"
                        href={TransferHelper.getAddressTransfersLink(
                          record.source?.address
                        )}
                      >
                        <span className="mr:4">{record.source?.address}</span>
                      </a>
                      {renderAddressTag(record.source?.category)}
                      <CopyIconButton
                        className="mr:4"
                        text={record.source?.address}
                      />
                    </>
                  )}
                </>
              ) : (
                "?"
              )}
            </div>
            <div>
              <SwapRightOutlined />
            </div>
            <div className="flex">
              {record.target?.address ? (
                <>
                  {record.target?.address != null &&
                  (record.target?.address.length != 34 ||
                    record.target?.address[0] != "T") ? (
                    <span className="mr:4 fg:gray-60 text:line-through">
                      {record.target?.address}
                    </span>
                  ) : (
                    <>
                      <a
                        target="_blank"
                        href={TransferHelper.getAddressTransfersLink(
                          record.target?.address
                        )}
                      >
                        <span className="mr:4">{record.target?.address}</span>
                      </a>
                      {renderAddressTag(record.target?.category)}
                      <CopyIconButton
                        className="mr:4"
                        text={record.target?.address}
                      />
                    </>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      },
    },
    {
      width: 90,
      className: "v:top t:right",
      title: t("domains.Transfer.depositAmount"),
      key: "amount",
      sorter: () => 1,
      render(_, { currency, amount0, amount }) {
        return (
          <>
            <div className="t:right">{DecimalHelper.fixed(amount, 2)}</div>
            <div className="t:right">{currency}</div>
            {amount0 !== amount && (
              <div className="t:right fg:gray-58">
                {DecimalHelper.fixed(amount0, 2)}
              </div>
            )}
          </>
        );
      },
    },
    {
      width: 90,
      className: "v:top t:right",
      title: t("domains.Transfer.depositResultAmount"),
      key: "result",
      sorter: () => 1,
      render(_, { currency, result0, result }) {
        return (
          result && (
            <>
              <div className="t:right">{DecimalHelper.fixed(result, 2)}</div>
              <div className="t:right">{currency}</div>
              {result0 !== result && (
                <div className="t:right fg:gray-58">
                  {DecimalHelper.fixed(result0, 2)}
                </div>
              )}
            </>
          )
        );
      },
    },
    {
      width: 80,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.expendAmount"),
      key: "expend",
      render(_, { currency, expend }) {
        return expend ? (
          <>
            <div className="t:right">{DecimalHelper.fixed(expend, 2)}</div>
            <div className="t:right">{CoinHelper.feeUnit(currency)}</div>
          </>
        ) : (
          <></>
        );
      },
    },
    {
      width: 80,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.chargeAmount"),
      key: "charge",
      render(_, { currency, charge }) {
        return (
          charge && (
            <>
              <div className="t:right">{DecimalHelper.fixed(charge, 2)}</div>
              <div className="t:right">{currency}</div>
            </>
          )
        );
      },
    },
    {
      className: "v:top",
      title: t("domains.Transfer.note"),
      key: "note",
      render(_, { note }) {
        return (
          <>
            <div className="w:full h:6.2857em overflow:hidden">
              {note && StringHelper.getLength(note) > 108
                ? StringHelper.ellipsisEnd(note, 108)
                : note}
            </div>
            {note && StringHelper.getLength(note) > 108 && (
              <Popconfirm
                className="abs bottom:8 right:4 bg:white p:5"
                title={t("systems.Modal.note")}
                description={
                  <>
                    <span className="inline-block w:400 mr:8 mb:8 word-break:break-all">
                      {note}
                    </span>
                  </>
                }
                showCancel={false}
                okText={t("systems.Action.confirm")}
              >
                <ExportOutlined />
              </Popconfirm>
            )}
          </>
        );
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.depositCreatedAt"),
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.checkedAt"),
      key: "checked_at",
      sorter: () => 1,
      render(_, { checked_at }) {
        return checked_at ? <div>{dayjs.$ldts(checked_at)}</div> : null;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.arrivedAt"),
      key: "arrived_at",
      sorter: () => 1,
      render(_, { arrived_at }) {
        return arrived_at ? <div>{dayjs.$ldts(arrived_at)}</div> : null;
      },
    },
    {
      width: 145,
      className: "v:top",
      title: t("domains.Transfer.status"),
      key: "status",
      render(_, record) {
        const TransactionEyeLink = ({ tran }: { tran: string }) => (
          <a
            className="ml:8"
            target="_blank"
            href={TransferHelper.getTransactionLink(tran)}
          >
            <EyeOutlined />
          </a>
        );

        const isCheckButtonNeeded =
          TransferHelper.isArrivedWithWrongAmount(record.status) &&
          auth.hasPerm(UserPermission.allow_to_check_member_deposit_records);

        const CheckButton = () => (
          <Button
            type="primary"
            onClick={() => checkModalRef.current?.open(record.id)}
          >
            {t("domains.Transfer.check")}
          </Button>
        );

        const MuteButton = () => (
          <Button
            type="primary"
            style={{ backgroundColor: "#eb8457" }}
            onClick={() => handleMute(record.id)}
          >
            {t("domains.Transfer.mute")}
          </Button>
        );

        const isCancelButtonNeeded =
          auth.hasPerm(UserPermission.allow_to_cancel_member_deposit_records) &&
          TransferHelper.isCreated(record.status);
        const CancelButton = () => (
          <Button
            danger
            type="primary"
            onClick={() => handleDestroy(record.id)}
          >
            {t("domains.Transfer.destroy")}
          </Button>
        );

        const isCorrectButtonNeeded = TransferHelper.isExpired(record.status);
        const CorrectButton = () => (
          <Button
            type="primary"
            style={{ backgroundColor: "#ff8c85" }}
            onClick={() => correctModalRef.current?.open(record)}
          >
            {t("domains.Transfer.correct")}
          </Button>
        );

        const isRecoverButtonNeededForStatusIsRefused =
          TransferHelper.isCheckedAndRefused(record.status) &&
          auth.hasPerm(
            UserPermission.allow_to_recover_member_deposit_records_when_status_is_refused
          );

        const RecoverButton = () => (
          <div className="flex gap:8">
            <Button
              type="primary"
              style={{ backgroundColor: "#6983e0" }}
              onClick={() => handleRecover(record.id)}
            >
              {t("domains.Transfer.recover")}
            </Button>
          </div>
        );

        const isCallbackButtonsNeeded =
          TransferHelper.isArrivedWithRightAmount(record.status) ||
          TransferHelper.isCheckedAndAllowed(record.status);

        const CallbacksButton = () => (
          <div className="flex gap:8">
            <Button type="primary" onClick={() => handleCallback(record.id)}>
              {t("domains.Transfer.callback")}
            </Button>
            {record.callback && (
              <Button
                type="primary"
                style={{ backgroundColor: "#6983e0" }}
                onClick={() => handleShowCallbackMessage(record.callback)}
              >
                {t("domains.Transfer.showCallbackMessage")}
              </Button>
            )}
          </div>
        );

        return (
          <>
            <div>
              <span
                className={TransferClassHelper.getStatusColor(record.status)}
              >
                {t(TransferDepositStatusI18nKeys[record.status])}
              </span>
              <span className="ml:8">
                <CopyIconButton text={record.transaction} />
              </span>
              {record.transaction &&
                record.transaction.split(",").map((tran, idx) => {
                  return <TransactionEyeLink tran={tran} key={idx} />;
                })}
            </div>
            <div>
              {isCheckButtonNeeded && (
                <>
                  <CheckButton /> <MuteButton />
                </>
              )}
              {isCancelButtonNeeded && <CancelButton />}
              {isCorrectButtonNeeded && <CorrectButton />}
              {isCallbackButtonsNeeded && <CallbacksButton />}
              {isRecoverButtonNeededForStatusIsRefused && <RecoverButton />}
            </div>
          </>
        );
      },
    },
  ];

  const tableStickyProps: TableProps["sticky"] = {
    offsetHeader: -16,
    offsetSummary: 24,
    offsetScroll: 24,
    getContainer() {
      return document.body.querySelector("main") as HTMLElement;
    },
  };

  const TableSummary = () => (
    <Table.Summary fixed="bottom">
      <Table.Summary.Row>
        <Table.Summary.Cell index={1} colSpan={3} />
        <Table.Summary.Cell index={4} align="right">
          {summary.amount.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          {summary.result.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} />
        <Table.Summary.Cell index={7} align="right">
          {summary.charge.toFixed(2)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={8} colSpan={5} />
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <div className="flex flex:col">
      <TableTopFilters />

      <Table
        rowKey="id"
        tableLayout="fixed"
        className="has-summary"
        sticky={tableStickyProps}
        loading={loading}
        columns={columns}
        dataSource={transfers}
        scroll={{ x: "1600px" }}
        rowClassName={getRowClassName}
        summary={TableSummary}
      ></Table>

      <CorrectModal ref={correctModalRef} onCheck={handleCorrectModalCheck} />

      <CheckModal ref={checkModalRef} onCheck={handleCheckModalCheck} />
    </div>
  );
};
